// hooks/use-calendar-settings.ts
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  useBookingRulesMutation,
  useScheduleMutation,
  useBookingRulesExtraMutation
} from "../api/settings/mutations";
import { settingsKeys } from "../api/settings/queries/keys";
import type { BookingRulesExtraResponse, BookingRulesResponse, ScheduleResponse } from "../api/settings/types";

// Consolidated settings state interface
interface SettingsState {
  // Booking Rules
  leadTime: string;
  interval: string;

  // Availability
  availability: Record<string, {
    available: boolean;
    startTime: string;
    endTime: string;
  }>;
  timezone: string;

  // Advanced
  sendReminders: boolean;
  meetingPrice: string;
}

export const useCalendarSettings = ({
  bookingRulesData,
  scheduleData,
  bookingRulesExtraData,
  onSuccess
}: {
  bookingRulesData?: BookingRulesResponse;
  scheduleData?: ScheduleResponse;
  bookingRulesExtraData?: BookingRulesExtraResponse;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  // Mutations
  const bookingRulesMutation = useBookingRulesMutation();
  const scheduleMutation = useScheduleMutation();
  const bookingRulesExtraMutation = useBookingRulesExtraMutation();

  // Single state object for all settings
  const [settings, setSettings] = useState<SettingsState>({
    // Booking Rules defaults
    leadTime: "1",
    interval: "15",

    // Availability defaults
    availability: {},
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

    // Advanced defaults
    sendReminders: false,
    meetingPrice: "0"
  });

  // Helper function to convert 12-hour to 24-hour format
  const convertTo24HourFormat = (time: string) => {
    if (!time) return "09:00";

    if (!time.toLowerCase().includes('am') && !time.toLowerCase().includes('pm')) {
      return time;
    }

    const isPM = time.toLowerCase().includes('pm');
    const isAM = time.toLowerCase().includes('am');
    const cleanTime = time.replace(/\s*[AaPp][Mm]\s*$/, '').trim();

    const [hourStr, minuteStr = "00"] = cleanTime.split(':');
    let hour = parseInt(hourStr || "9");

    if (isPM && hour < 12) {
      hour += 12;
    } else if (isAM && hour === 12) {
      hour = 0;
    }

    return `${hour.toString().padStart(2, '0')}:${minuteStr}`;
  };

  // Initialize settings from API data
  useEffect(() => {
    const newSettings: Partial<SettingsState> = {};

    // Initialize booking rules
    if (bookingRulesData?.booking_rules) {
      newSettings.leadTime = bookingRulesData.booking_rules.lead_time_days?.toString() || "1";
      newSettings.interval = bookingRulesData.booking_rules.time_interval_minutes?.toString() || "15";
    }

    // Initialize availability
    if (scheduleData?.schedule?.weekly_schedule) {
      const availability: Record<string, { available: boolean; startTime: string; endTime: string }> = {};

      const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

      days.forEach(day => {
        const dayData = scheduleData.schedule.weekly_schedule[day.toLowerCase()] ||
          scheduleData.schedule.weekly_schedule[day];

        if (dayData) {
          availability[day] = {
            available: dayData.available ?? false,
            startTime: convertTo24HourFormat(dayData.from_time || "09:00"),
            endTime: convertTo24HourFormat(dayData.to_time || "17:00")
          };
        } else {
          availability[day] = {
            available: false,
            startTime: "09:00",
            endTime: "17:00"
          };
        }
      });

      newSettings.availability = availability;
    }

    // Initialize advanced settings
    if (bookingRulesExtraData?.booking_rules_extra) {
      newSettings.sendReminders = bookingRulesExtraData.booking_rules_extra.send_patient_reminders ?? false;
      newSettings.meetingPrice = bookingRulesExtraData.booking_rules_extra.booking_price?.toString() || "0";
    }

    if (scheduleData?.schedule?.timezone) {
      setSettings(prev => ({ ...prev, ...newSettings, timezone: scheduleData?.schedule?.timezone }));
    }

    // Only update if we have new data
    if (Object.keys(newSettings).length > 0) {
      setSettings(prev => ({ ...prev, ...newSettings }));
    }
  }, [bookingRulesData, scheduleData, bookingRulesExtraData]);

  // Update functions
  const updateBookingRules = (updates: Partial<Pick<SettingsState, 'leadTime' | 'interval'>>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  };

  const updateAvailability = (
    day: string,
    field: 'available' | 'startTime' | 'endTime',
    value: string | boolean
  ) => {
    setSettings(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          [field]: value
        } as { available: boolean; startTime: string; endTime: string }
      }
    }));
  };

  const updateTimezone = (timezone: string) => {
    setSettings(prev => ({ ...prev, timezone }));
  };

  const updateAdvanced = (updates: Partial<Pick<SettingsState, 'sendReminders' | 'meetingPrice'>>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  };

  // Format time for API (handles both 12-hour and 24-hour input)
  const formatTimeForAPI = (timeString: string) => {
    const timeRegex = /^(\d{1,2}):(\d{2})$/;
    const match = timeString.match(timeRegex);
    if (match) {
      const hour = parseInt(match[1]!);
      const minute = parseInt(match[2]!);
      return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    }
    return convertTo24HourFormat(timeString);
  };

  // Save all settings
  const saveAllSettings = async () => {
    const errors: string[] = [];

    try {
      // Save booking rules
      try {
        await bookingRulesMutation.mutateAsync({
          lead_time_days: parseInt(settings.leadTime),
          time_interval_minutes: parseInt(settings.interval)
        });
        queryClient.invalidateQueries({ queryKey: settingsKeys.bookingRules() });
      } catch (error) {
        console.error('Booking rules error:', error);
        errors.push('booking rules');
      }

      // Save availability
      try {
        const transformedAvailability = Object.entries(settings.availability).reduce((acc, [day, value]) => ({
          ...acc,
          [day.toLowerCase()]: {
            available: value.available,
            from_time: formatTimeForAPI(value.startTime),
            to_time: formatTimeForAPI(value.endTime)
          }
        }), {});

        await scheduleMutation.mutateAsync({
          weekly_schedule: transformedAvailability,
          timezone: settings.timezone
        });
        queryClient.invalidateQueries({ queryKey: settingsKeys.schedules() });
      } catch (error) {
        console.error('Schedule error:', error);
        errors.push('availability');
      }

      // Save advanced settings
      try {
        await bookingRulesExtraMutation.mutateAsync({
          booking_price: parseFloat(settings.meetingPrice) || 0,
          send_patient_reminders: settings.sendReminders
        });
        queryClient.invalidateQueries({ queryKey: settingsKeys.bookingRulesExtra() });
      } catch (error) {
        console.error('Advanced settings error:', error);
        errors.push('advanced settings');
      }

      if (errors.length === 0) {
        toast.success("Calendar settings updated successfully");
        onSuccess?.();
      } else {
        toast.error(`Failed to save: ${errors.join(', ')}`);
      }

    } catch (error) {
      console.error('Settings save error:', error);
      toast.error("Failed to save calendar settings");
      throw error;
    }
  };

  const isLoading = bookingRulesMutation.isPending ||
    scheduleMutation.isPending ||
    bookingRulesExtraMutation.isPending;

  return {
    // State
    settings,

    // Update functions
    updateBookingRules,
    updateAvailability,
    updateTimezone,
    updateAdvanced,

    // Save function
    saveAllSettings,

    // Status
    isLoading,

    // Helper functions
    formatTimeInput: formatTimeForAPI
  };
};